// change-password-modal.component.ts
import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { authBackendEndPointToken } from '../app.config';
import { AuthService } from '../shared/auth/auth.service';
import { SessionService } from '../shared/auth/session.service';
@Component({
  selector: 'app-change-password-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule
  ],
  template: `
    <div class="change-password-modal">
      <div class="modal-header">
        <h2 mat-dialog-title>Change Password</h2>
        <button mat-icon-button (click)="onClose()" class="close-button">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <mat-dialog-content>
        <div class="modal-description" *ngIf="!changeSuccess">
          Enter your current password and choose a new password.
        </div>

        <div class="success-message" *ngIf="changeSuccess">
          <mat-icon class="success-icon">check_circle</mat-icon>
          <p>Your password has been changed successfully!</p>
          <p class="sub-text">You can now use your new password to sign in.</p>
        </div>

        <form [formGroup]="changePasswordForm" (ngSubmit)="onSubmit()" *ngIf="!changeSuccess">
          <!-- Current Password -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Current Password</mat-label>
            <mat-icon matPrefix>lock</mat-icon>
            <input
              matInput
              [type]="showCurrentPassword ? 'text' : 'password'"
              formControlName="currentPassword"
              required />
            <button
              mat-icon-button
              matSuffix
              (click)="showCurrentPassword = !showCurrentPassword"
              type="button">
              <mat-icon>{{ showCurrentPassword ? 'visibility_off' : 'visibility' }}</mat-icon>
            </button>
            <mat-error *ngIf="changePasswordForm.get('currentPassword')?.hasError('required')">
              Current password is required
            </mat-error>
          </mat-form-field>

          <!-- New Password -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>New Password</mat-label>
            <mat-icon matPrefix>lock</mat-icon>
            <input
              matInput
              [type]="showNewPassword ? 'text' : 'password'"
              formControlName="newPassword"
              required />
            <button
              mat-icon-button
              matSuffix
              (click)="showNewPassword = !showNewPassword"
              type="button">
              <mat-icon>{{ showNewPassword ? 'visibility_off' : 'visibility' }}</mat-icon>
            </button>
            <mat-error *ngIf="changePasswordForm.get('newPassword')?.hasError('required')">
              New password is required
            </mat-error>
            <mat-error *ngIf="changePasswordForm.get('newPassword')?.hasError('minlength')">
              Password must be at least 6 characters
            </mat-error>
          </mat-form-field>

          <!-- Confirm New Password -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Confirm New Password</mat-label>
            <mat-icon matPrefix>lock</mat-icon>
            <input
              matInput
              [type]="showConfirmPassword ? 'text' : 'password'"
              formControlName="confirmPassword"
              required />
            <button
              mat-icon-button
              matSuffix
              (click)="showConfirmPassword = !showConfirmPassword"
              type="button">
              <mat-icon>{{ showConfirmPassword ? 'visibility_off' : 'visibility' }}</mat-icon>
            </button>
            <mat-error *ngIf="changePasswordForm.get('confirmPassword')?.hasError('required')">
              Please confirm your password
            </mat-error>
            <mat-error *ngIf="changePasswordForm.get('confirmPassword')?.hasError('passwordMismatch')">
              Passwords do not match
            </mat-error>
          </mat-form-field>

          <!-- Password Requirements -->
          <div class="password-requirements">
            <div class="requirement-title">Password must contain:</div>
            <div class="requirement-item" [class.met]="hasMinLength">
              <mat-icon>{{ hasMinLength ? 'check_circle' : 'radio_button_unchecked' }}</mat-icon>
              <span>At least 6 characters</span>
            </div>
          </div>

          <!-- Error Message -->
          <div class="error-message" *ngIf="errorMessage">
            <mat-icon>error</mat-icon>
            <span>{{ errorMessage }}</span>
          </div>
        </form>
      </mat-dialog-content>

      <mat-dialog-actions align="end">
        <button
          mat-button
          (click)="onClose()"
          type="button"
          class="cancel-button">
          {{ changeSuccess ? 'Close' : 'Cancel' }}
        </button>
        <button
          mat-flat-button
          color="primary"
          class="primary-button"
          (click)="onSubmit()"
          [disabled]="loading || changeSuccess || changePasswordForm.invalid"
          *ngIf="!changeSuccess">
          {{ loading ? 'Changing...' : 'Change Password' }}
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [`
    .change-password-modal {
      min-width: 450px;
      max-width: 550px;
    }

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 24px 0;

      h2 {
        margin: 0;
        font-size: 24px;
        font-weight: 600;
        color: #1a1a1a;
      }

      .close-button {
        margin-right: -12px;
      }
    }

    mat-dialog-content {
      padding: 20px 24px;
      max-height: 500px;
      overflow-y: auto;
    }

    .modal-description {
      color: #666;
      font-size: 14px;
      margin-bottom: 24px;
      line-height: 1.5;
    }

    .success-message {
      text-align: center;
      padding: 20px 0;

      .success-icon {
        font-size: 64px;
        width: 64px;
        height: 64px;
        color: #4caf50;
        margin-bottom: 16px;
      }

      p {
        margin: 8px 0;
        color: #333;
        font-size: 15px;
        line-height: 1.5;
      }

      .sub-text {
        color: #666;
        font-size: 13px;
      }
    }

    .full-width {
      width: 100%;
      margin-bottom: 16px;
    }

    .password-requirements {
      background: #f5f5f5;
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 16px;

      .requirement-title {
        font-size: 13px;
        font-weight: 600;
        color: #333;
        margin-bottom: 12px;
      }

      .requirement-item {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 13px;
        color: #666;
        margin-bottom: 8px;

        &:last-child {
          margin-bottom: 0;
        }

        mat-icon {
          font-size: 18px;
          width: 18px;
          height: 18px;
          color: #ccc;
        }

        &.met {
          color: #4caf50;

          mat-icon {
            color: #4caf50;
          }
        }
      }
    }

    .error-message {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #d32f2f;
      font-size: 13px;
      margin-top: 8px;
      padding: 8px 12px;
      background-color: #ffebee;
      border-radius: 4px;

      mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
      }
    }

    mat-dialog-actions {
      padding: 12px 24px 20px;
      margin: 0;

      .cancel-button {
        margin-right: 8px;
      }

      .primary-button {
        min-width: 150px;
      }
    }

    ::ng-deep .mat-mdc-dialog-container {
      border-radius: 12px !important;
    }

    @media (max-width: 600px) {
      .change-password-modal {
        min-width: 100%;
      }
    }
  `]
})
export class ChangePasswordModalComponent {
  changePasswordForm: FormGroup;
  loading = false;
  changeSuccess = false;
  errorMessage = '';
  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;

  private fb = inject(FormBuilder);
  private http = inject(HttpClient);
  private dialogRef = inject(MatDialogRef<ChangePasswordModalComponent>);
  private snackBar = inject(MatSnackBar);
 private authService = inject(AuthService);
  private sessionService = inject(SessionService);

  authBackendApiBaseUrl = inject(authBackendEndPointToken)

  constructor() {
    this.changePasswordForm = this.fb.group({
      currentPassword: ['', [Validators.required]],
      newPassword: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });
  }

  // Custom validator to check if passwords match
  passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const newPassword = control.get('newPassword');
    const confirmPassword = control.get('confirmPassword');

    if (!newPassword || !confirmPassword) {
      return null;
    }

    if (confirmPassword.value === '') {
      return null;
    }

    if (newPassword.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    } else {
      const errors = confirmPassword.errors;
      if (errors) {
        delete errors['passwordMismatch'];
        if (Object.keys(errors).length === 0) {
          confirmPassword.setErrors(null);
        }
      }
      return null;
    }
  }

  get hasMinLength(): boolean {
    const password = this.changePasswordForm.get('newPassword')?.value || '';
    return password.length >= 6;
  }

  onSubmit(): void {
    if (this.changePasswordForm.invalid || this.loading || this.changeSuccess) {
      this.changePasswordForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    const requestBody = {
      currentPassword: this.changePasswordForm.value.currentPassword,
      newPassword: this.changePasswordForm.value.newPassword
    };

    // Update this URL to match your API endpoint

    this.http.post(`${this.authBackendApiBaseUrl}/api/auth/change-password`, requestBody)
      .subscribe({
        next: (response: any) => {
          console.log('✅ Password changed successfully');
          this.loading = false;
          this.changeSuccess = true;


             const currentUser = this.authService.currentUserValue;
          if (currentUser) {
            // Update the user object in AuthService
            const updatedUser = {
              ...currentUser,
              isDefaultPassword: false
            };

            this.authService.user.next(updatedUser);

            // Also update session
            const session = this.sessionService.getSession();
            if (session) {
              this.sessionService.setSession({
                ...session,
                isDefaultPassword: false
              });
            }

            console.log('✅ Updated isDefaultPassword flag to false');
          }

          // Show success snackbar
          this.snackBar.open('Password changed successfully!', 'Close', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['success-snackbar']
          });

          // Auto-close modal after 2 seconds
          setTimeout(() => {
            this.dialogRef.close({ success: true });
          }, 2000);
        },
        error: (error: any) => {
          console.error('❌ Change password error:', error);
          this.loading = false;

          if (error.status === 400) {
            const message = error.error?.message || '';
            if (message.includes('Current password is incorrect')) {
              this.errorMessage = 'Current password is incorrect.';
            } else {
              this.errorMessage = message || 'Failed to change password. Please try again.';
            }
          } else if (error.status === 401) {
            this.errorMessage = 'Session expired. Please login again.';
          } else {
            this.errorMessage = 'Failed to change password. Please try again.';
          }
        }
      });
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
